import { z } from "zod";

/**
 * MÜVEKKİL / HİZMET İLİŞKİSİ / PORTFÖY AI İÇGÖRÜLERİ (CLOS-289 `[193]` · CLOS-1017/1018).
 *
 * ### ⚠ İÇGÖRÜ BİR KAYIT DEĞİL
 *
 * Kartın kapsam sınırı: *"AI müvekkil kaydı, iletişim kişisi, temsil yetkisi,
 * sponsorluk, hizmet ilişkisi, sözleşme erişimi, portföy sorumluluğu ... oluşturmaz/
 * değiştirmez/taşımaz."* Şemada bu yüzden ne bir "birleştir" ne bir "ata" alanı var.
 *
 * ### ⚠ MÜKERRER İDDİASI DETERMİNİSTİK OLARAK DOĞRULANIYOR
 *
 * Kart: *"Belgeden çıkarılan profil alanı veya mükerrer eşleşme YALNIZ ÖNERİDİR;
 * doğrulanmış kimlik veya otomatik merge kararı değildir."* Model iki kaydın aynı kişi
 * olduğunu düşünebilir; sistem bunu `findClientDuplicates`'in (kanonik, normalize edilmiş
 * kimlik/e-posta/telefon eşleşmesi) sonucuyla KARŞILAŞTIRIYOR ve `duplicateVerified`
 * alanına yazıyor. Doğrulanmamış bir mükerrer iddiası ekranda ayrıca işaretleniyor —
 * çünkü yanlış birleştirme geri alınması en pahalı işlemlerden biri.
 */

export const AI_CLIENT_INSIGHT_CATEGORIES = [
  "profile_gap",
  "possible_duplicate",
  "workload",
  "scope_gap",
  "relationship_risk",
] as const;
export type AiClientInsightCategory = (typeof AI_CLIENT_INSIGHT_CATEGORIES)[number];

export const AI_CLIENT_INSIGHT_CATEGORY_NAMES: Record<AiClientInsightCategory, string> = {
  profile_gap: "Eksik bilgi",
  possible_duplicate: "Olası mükerrer kayıt",
  workload: "İş yükü",
  scope_gap: "Kapsam boşluğu",
  relationship_risk: "İlişki riski",
};

export const AI_CLIENT_INSIGHT_PRIORITIES = ["high", "medium", "low"] as const;
export type AiClientInsightPriority = (typeof AI_CLIENT_INSIGHT_PRIORITIES)[number];

export const AI_CLIENT_INSIGHT_PRIORITY_NAMES: Record<AiClientInsightPriority, string> = {
  high: "Önce bunu inceleyin",
  medium: "İncelenmeli",
  low: "Bilgi amaçlı",
};

/**
 * ⚠ EKRANDA ZORUNLU. Müvekkil alanında bir AI çıktısının "gerçek" gibi okunması, yanlış
 * bir birleştirmeye ya da yanlış bir temsil kararına dönüşebilir.
 */
export const AI_CLIENT_INSIGHT_ADVISORY =
  "Bu içgörüler bir kayıt, temsil ya da hizmet kararı değildir; hiçbiri kendiliğinden " +
  "uygulanmaz. Değişikliği ilgili müvekkil, temsil veya hizmet ekranından siz yaparsınız.";

export const AI_CLIENT_INSIGHT_EMPTY_MESSAGE =
  "Bu taramada öne çıkan bir içgörü çıkmadı. Bu, portföyünüzde eksik ya da riskli bir " +
  "kayıt olmadığı anlamına gelmez.";

/** ⚠ Doğrulanmamış mükerrer iddiasının ekrandaki cümlesi. */
export const AI_CLIENT_DUPLICATE_UNVERIFIED_WARNING =
  "Bu mükerrer iddiası sistemin kimlik/iletişim eşleşmesiyle DOĞRULANMADI; birleştirmeden " +
  "önce iki kaydı elle karşılaştırın.";

export const aiClientInsightSchema = z.strictObject({
  category: z.enum(AI_CLIENT_INSIGHT_CATEGORIES),
  priority: z.enum(AI_CLIENT_INSIGHT_PRIORITIES),
  confidence: z.enum(["high", "medium", "low"]),
  title: z.string().min(1).max(200),
  rationale: z.string().min(1).max(1500),
  /** İçgörünün baktığı müvekkilin GÖRÜNEN ADI — kimliği ayrı alanda. */
  subject: z.string().min(1).max(200),
  /** ⚠ İsteğe bağlı: iş yükü gibi bazı içgörüler tek bir kayda bağlı değil. */
  clientId: z.string().max(200).nullish(),
  /** ⚠ Yalnız `possible_duplicate` için: iddia edilen ikinci kayıt. */
  duplicateOfClientId: z.string().max(200).nullish(),
});
export type AiClientInsight = z.infer<typeof aiClientInsightSchema>;

export const aiClientInsightOutputSchema = z.strictObject({
  insights: z.array(aiClientInsightSchema).max(30),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(10).default([]),
});
export type AiClientInsightOutput = z.infer<typeof aiClientInsightOutputSchema>;

export type AiClientInsightView = AiClientInsight & {
  /**
   * ⚠ AI KESİN BİR İFADE KULLANDI MI (CLOS-1088). Cümle SİLİNMİYOR ya da düzeltilmiyor —
   * işaretleniyor; risk ailesinin `certaintyFlagged` kararıyla aynı gerekçe: çıktıyı
   * reddetmek kullanıcının kredisini bir kelime seçimi yüzünden yakardı, sessizce
   * düzeltmek AI'ın ne dediğini kullanıcıdan gizlerdi.
   */
  certaintyFlagged: boolean;

  /**
   * ⚠ `null` = mükerrer iddiası DEĞİL (kategori başka). `false` = iddia var ama kanonik
   * eşleşme onu DOĞRULAMADI. `true` = kanonik eşleşme de aynı kaydı gösteriyor.
   */
  duplicateVerified: boolean | null;
  /** Müvekkile bağlı sözleşme sayısı — "iş yükü" iddiasının doğrulanabilir ölçüsü. */
  linkedContracts: number;
};

export type AiClientInsightRunSummary = {
  id: string;
  status: "completed" | "failed";
  insights: AiClientInsightView[];
  reviewNeeded: string[];
  errorCode: "provider_error" | "invalid_output" | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  createdByName: string;
  createdAt: string;
};

export const AI_CLIENT_INSIGHT_ERROR_MESSAGES: Record<"provider_error" | "invalid_output", string> =
  {
    provider_error: "AI sağlayıcısına ulaşılamadı; içgörü üretilemedi ve krediniz iade edildi.",
    invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
  };
